import { ProductListLink } from '@graphcommerce/magento-product'
import { Row, extendableComponent } from '@graphcommerce/next-ui'
import { Box, SxProps, Theme, Typography } from '@mui/material'
import React from 'react'
import { CategoryHeroNavWithImageFragment } from '../../graphql/CategoryHeroNavWithImage.gql'
import {Image} from '@graphcommerce/image'

export type CategoryHeroNavProps = {
  title: React.ReactNode
  asset?: React.ReactNode
  sx?: SxProps<Theme>
} & CategoryHeroNavWithImageFragment

const cmpName = 'CategoryHeroNav' as const
const parts = ['wrapper', 'categories', 'title', 'placeholder'] as const
const { classes } = extendableComponent(cmpName, parts)

export function BrandsHeroNav({ children, title, asset, sx = [] }: CategoryHeroNavProps) {
  return (
    <Row
      className={classes.wrapper}
      maxWidth={false}
      sx={[
        (theme) => ({
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateAreas: `
            "title"
            "categories"
          `,
          gridTemplateRows: 'auto auto',
          borderBottom: `1px solid ${theme.palette.divider}`,
          marginBottom: theme.spacings.xxl,
          paddingBottom: theme.page.vertical,
          [theme.breakpoints.up('md')]: {
            width: '100%',
            paddingRight: theme.page.horizontal,
            paddingTop: 0,
            gridColumnGap: 0,
            display: 'grid',
            gridTemplateColumns: '1fr',
            gridTemplateRows: 'auto auto',
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        className={classes.categories}
        sx={(theme) => ({
          gridArea: 'categories',
          display: 'grid',
          gridColumnGap: theme.spacings.xxl,
          gridRowGap: theme.spacings.lg,
          height: 'min-content',
          maxWidth: '80vw',
          justifySelf: 'center',
          alignSelf: 'start',
          gridTemplateColumns: '1fr 1fr',
          marginBottom: theme.spacings.lg,
          [theme.breakpoints.up('md')]: {
            margin: 0,
            gridColumnGap: theme.spacings.sm,
            gridRowGap: theme.spacings.sm,
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            maxWidth: '100vw',
            width: '100%',
            justifySelf: 'start',
          },
        })}
      >
        {children?.map((category) => {
          if (!category?.url_path || !category.uid || !category.name || !category.include_in_menu)
            return null
          return (
            <ProductListLink
              underline='none'
              color='textPrimary'
              url={category.url_path}
              filters={{ category_uid: { eq: category.uid } }}
              sort={{}}
              key={category.uid}
              sx={(theme) => ({
                display: 'grid',
                gridTemplateRows: 'auto 1fr',
                alignItems: 'center'
              })}
            >
              <Box sx={{ aspectRatio: '4 / 3', position: 'relative' }}>
                {category.image && (
                  <Image layout='fill' sx={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '90%',
                    maxHeight: '180px',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                         src={category.image} alt={category.name}
                  />
                )}
              </Box>
              <Typography variant='h4' component='span' sx={(theme) => ({
                display: 'block',
                textAlign: 'center'
              })}>
                {category.name}
              </Typography>
            </ProductListLink>
          )
        })}
      </Box>
    </Row>
  )
}
